export interface EmailTemplate {
  subject: string;
  body: string;
  html?: string;
}

export interface EmailData {
  to: string;
  from: string;
  subject: string;
  body: string;
  html?: string;
  attachments?: Array<{
    filename: string;
    content: string | Buffer;
    contentType: string;
  }>;
}

export interface InvoiceEmailData {
  invoiceNumber: string;
  clientName: string;
  clientEmail: string;
  amount: number;
  dueDate: string;
  pdfUrl?: string;
  companyName: string;
  companyEmail: string;
}

export interface ReminderEmailData {
  invoiceNumber: string;
  clientName: string;
  clientEmail: string;
  amount: number;
  dueDate: string;
  daysOverdue: number;
  companyName: string;
  companyEmail: string;
}

export interface PaymentConfirmationData {
  invoiceNumber: string;
  clientName: string;
  clientEmail: string;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  companyName: string;
  companyEmail: string;
}

class EmailService {
  private apiKey: string;
  private baseUrl: string;

  constructor() {
    this.apiKey = process.env.REACT_APP_EMAIL_API_KEY || '';
    this.baseUrl = process.env.REACT_APP_EMAIL_API_URL || 'https://api.emailservice.com';
  }

  // Send a generic email
  async sendEmail(emailData: EmailData): Promise<boolean> {
    try {
      // Simulate API call for development
      console.log('Sending email:', emailData);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In production, this would make an actual API call
      // const response = await fetch(`${this.baseUrl}/send`, {
      //   method: 'POST',
      //   headers: {
      //     'Authorization': `Bearer ${this.apiKey}`,
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify(emailData),
      // });
      
      return true;
    } catch (error) {
      console.error('Failed to send email:', error);
      return false;
    }
  }

  // Send invoice email
  async sendInvoiceEmail(data: InvoiceEmailData): Promise<boolean> {
    const subject = `Invoice #${data.invoiceNumber} from ${data.companyName}`;
    const body = this.generateInvoiceEmailBody(data);
    const html = this.generateInvoiceEmailHTML(data);

    return this.sendEmail({
      to: data.clientEmail,
      from: data.companyEmail,
      subject,
      body,
      html,
    });
  }

  // Send payment reminder
  async sendPaymentReminder(data: ReminderEmailData): Promise<boolean> {
    const subject = `Payment Reminder - Invoice #${data.invoiceNumber}`;
    const body = this.generateReminderEmailBody(data);
    const html = this.generateReminderEmailHTML(data);

    return this.sendEmail({
      to: data.clientEmail,
      from: data.companyEmail,
      subject,
      body,
      html,
    });
  }

  // Send payment confirmation
  async sendPaymentConfirmation(data: PaymentConfirmationData): Promise<boolean> {
    const subject = `Payment Confirmation - Invoice #${data.invoiceNumber}`;
    const body = this.generatePaymentConfirmationBody(data);
    const html = this.generatePaymentConfirmationHTML(data);

    return this.sendEmail({
      to: data.clientEmail,
      from: data.companyEmail,
      subject,
      body,
      html,
    });
  }

  // Generate invoice email body (plain text)
  private generateInvoiceEmailBody(data: InvoiceEmailData): string {
    return `
Dear ${data.clientName},

Thank you for your business! Please find attached invoice #${data.invoiceNumber} for the amount of $${data.amount.toFixed(2)}.

Invoice Details:
- Invoice Number: ${data.invoiceNumber}
- Amount Due: $${data.amount.toFixed(2)}
- Due Date: ${new Date(data.dueDate).toLocaleDateString()}

You can view and pay this invoice online by clicking the link below:
${data.pdfUrl || 'https://c-band.com/invoice/' + data.invoiceNumber}

If you have any questions about this invoice, please don't hesitate to contact us.

Best regards,
${data.companyName}
${data.companyEmail}
    `.trim();
  }

  // Generate invoice email HTML
  private generateInvoiceEmailHTML(data: InvoiceEmailData): string {
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Invoice #${data.invoiceNumber}</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #3b82f6; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; }
        .invoice-details { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; }
        .amount { font-size: 24px; font-weight: bold; color: #3b82f6; }
        .button { display: inline-block; background: #3b82f6; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
        .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 14px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Invoice #${data.invoiceNumber}</h1>
            <p>${data.companyName}</p>
        </div>
        <div class="content">
            <p>Dear ${data.clientName},</p>
            <p>Thank you for your business! Please find your invoice details below.</p>
            
            <div class="invoice-details">
                <h3>Invoice Details</h3>
                <p><strong>Invoice Number:</strong> ${data.invoiceNumber}</p>
                <p><strong>Amount Due:</strong> <span class="amount">$${data.amount.toFixed(2)}</span></p>
                <p><strong>Due Date:</strong> ${new Date(data.dueDate).toLocaleDateString()}</p>
            </div>
            
            <a href="${data.pdfUrl || 'https://c-band.com/invoice/' + data.invoiceNumber}" class="button">
                View & Pay Invoice
            </a>
            
            <p>If you have any questions about this invoice, please don't hesitate to contact us.</p>
            
            <div class="footer">
                <p>Best regards,<br>${data.companyName}</p>
                <p>${data.companyEmail}</p>
            </div>
        </div>
    </div>
</body>
</html>
    `.trim();
  }

  // Generate reminder email body
  private generateReminderEmailBody(data: ReminderEmailData): string {
    return `
Dear ${data.clientName},

This is a friendly reminder that invoice #${data.invoiceNumber} for $${data.amount.toFixed(2)} is ${data.daysOverdue > 0 ? `${data.daysOverdue} day(s) overdue` : 'due today'}.

Invoice Details:
- Invoice Number: ${data.invoiceNumber}
- Amount Due: $${data.amount.toFixed(2)}
- Due Date: ${new Date(data.dueDate).toLocaleDateString()}
- Status: ${data.daysOverdue > 0 ? `${data.daysOverdue} day(s) overdue` : 'Due today'}

Please process this payment as soon as possible to avoid any late fees.

You can pay online at: https://c-band.com/invoice/${data.invoiceNumber}

If you have already made this payment, please disregard this reminder.

Thank you for your prompt attention to this matter.

Best regards,
${data.companyName}
${data.companyEmail}
    `.trim();
  }

  // Generate reminder email HTML
  private generateReminderEmailHTML(data: ReminderEmailData): string {
    const statusText = data.daysOverdue > 0 ? `${data.daysOverdue} day(s) overdue` : 'Due today';
    const statusColor = data.daysOverdue > 0 ? '#ef4444' : '#f59e0b';

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Payment Reminder - Invoice #${data.invoiceNumber}</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: ${statusColor}; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; }
        .reminder-details { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid ${statusColor}; }
        .amount { font-size: 24px; font-weight: bold; color: ${statusColor}; }
        .button { display: inline-block; background: ${statusColor}; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
        .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 14px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Payment Reminder</h1>
            <p>Invoice #${data.invoiceNumber}</p>
        </div>
        <div class="content">
            <p>Dear ${data.clientName},</p>
            <p>This is a friendly reminder that your invoice is ${data.daysOverdue > 0 ? `${data.daysOverdue} day(s) overdue` : 'due today'}.</p>
            
            <div class="reminder-details">
                <h3>Invoice Details</h3>
                <p><strong>Invoice Number:</strong> ${data.invoiceNumber}</p>
                <p><strong>Amount Due:</strong> <span class="amount">$${data.amount.toFixed(2)}</span></p>
                <p><strong>Due Date:</strong> ${new Date(data.dueDate).toLocaleDateString()}</p>
                <p><strong>Status:</strong> <span style="color: ${statusColor};">${statusText}</span></p>
            </div>
            
            <a href="https://c-band.com/invoice/${data.invoiceNumber}" class="button">
                Pay Now
            </a>
            
            <p>Please process this payment as soon as possible to avoid any late fees.</p>
            
            <div class="footer">
                <p>Best regards,<br>${data.companyName}</p>
                <p>${data.companyEmail}</p>
            </div>
        </div>
    </div>
</body>
</html>
    `.trim();
  }

  // Generate payment confirmation body
  private generatePaymentConfirmationBody(data: PaymentConfirmationData): string {
    return `
Dear ${data.clientName},

Thank you for your payment! We have received your payment for invoice #${data.invoiceNumber}.

Payment Details:
- Invoice Number: ${data.invoiceNumber}
- Amount Paid: $${data.amount.toFixed(2)}
- Payment Method: ${data.paymentMethod}
- Payment Date: ${new Date(data.paymentDate).toLocaleDateString()}

Your payment has been processed successfully. A receipt has been generated and is available for download.

If you have any questions about this payment, please don't hesitate to contact us.

Thank you for your business!

Best regards,
${data.companyName}
${data.companyEmail}
    `.trim();
  }

  // Generate payment confirmation HTML
  private generatePaymentConfirmationHTML(data: PaymentConfirmationData): string {
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Payment Confirmation - Invoice #${data.invoiceNumber}</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #10b981; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; }
        .payment-details { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #10b981; }
        .amount { font-size: 24px; font-weight: bold; color: #10b981; }
        .button { display: inline-block; background: #10b981; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
        .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 14px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Payment Confirmation</h1>
            <p>Thank you for your payment!</p>
        </div>
        <div class="content">
            <p>Dear ${data.clientName},</p>
            <p>We have received your payment for invoice #${data.invoiceNumber}. Thank you!</p>
            
            <div class="payment-details">
                <h3>Payment Details</h3>
                <p><strong>Invoice Number:</strong> ${data.invoiceNumber}</p>
                <p><strong>Amount Paid:</strong> <span class="amount">$${data.amount.toFixed(2)}</span></p>
                <p><strong>Payment Method:</strong> ${data.paymentMethod}</p>
                <p><strong>Payment Date:</strong> ${new Date(data.paymentDate).toLocaleDateString()}</p>
            </div>
            
            <a href="https://c-band.com/receipt/${data.invoiceNumber}" class="button">
                Download Receipt
            </a>
            
            <p>Your payment has been processed successfully. A receipt has been generated and is available for download.</p>
            
            <div class="footer">
                <p>Thank you for your business!<br>${data.companyName}</p>
                <p>${data.companyEmail}</p>
            </div>
        </div>
    </div>
</body>
</html>
    `.trim();
  }

  // Send bulk emails (for reminders, etc.)
  async sendBulkEmails(emails: EmailData[]): Promise<{ success: number; failed: number }> {
    let success = 0;
    let failed = 0;

    for (const email of emails) {
      const result = await this.sendEmail(email);
      if (result) {
        success++;
      } else {
        failed++;
      }
    }

    return { success, failed };
  }

  // Schedule email (for future sending)
  async scheduleEmail(emailData: EmailData, sendAt: Date): Promise<boolean> {
    try {
      console.log('Scheduling email for:', sendAt, emailData);
      await new Promise(resolve => setTimeout(resolve, 500));
      return true;
    } catch (error) {
      console.error('Failed to schedule email:', error);
      return false;
    }
  }
}

export const emailService = new EmailService();
